import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import { PublisherSideBarState } from '@App/enum/publisher-side-bar-state'
import { adminPublisherRemover, originalPublisherRemover } from '@App/helpers/work-entities/removers'
import './index.scss'
import { generatePublishersTableSchema } from '@Entities/schemas/wizardFlow/publishers-table-schema'
import TablesForm from '@Components/ViewWork/Table/TablesForm'
import AddPublishersTableInfo from '@Scenes/WorkScenes/Steps/PublishersSteps/PublishersStep/AddPublishersArea/AddPublishersTableInfo/AddPublishersTableInfo'
import CustomToolTip from '@Components/Tooltips/CustomToolTip'
import { TOOLTIP_TEXT } from '@Const/tooltip-text'
import { UseFormReset, FieldValues } from 'react-hook-form'
import SharesInfo from '@Components/ViewWork/SharesInfo'
import calculateTotalKnownShares from '@App/helpers/work-entities/utils/calculate-total-known-shares'
import { PublisherModel } from '@Entities/work/publisher-model'

const AddPublishersTable = () => {
    const { workStore, popupStore, rightsholderStore } = useContext<RootStore>(StoresContext)

    const workDraftFromLocal = workStore.workDraftLocal
    if (!workDraftFromLocal) {
        return <></>
    }

    const [tableIsValid, setTableIsValid] = useState<boolean>(false)

    const originPublishers = workDraftFromLocal.originalPublishers

    const stepValidator = () => {
        workStore.setWizardStepValidation(workDraftFromLocal.originalPublishers.length > 0 && tableIsValid)
    }

    useEffect(() => {
        stepValidator()
    }, [workStore.workDraftLocal, tableIsValid])

    const removeOriginalPublisher = (removingOP: PublisherModel) => {
        if (!workStore.workDraftLocal) {
            return
        }
        const updatedWorkDraft = originalPublisherRemover(removingOP, workDraftFromLocal)
        workStore.setWorkDraftToLocal(updatedWorkDraft)
    }

    const getOnRemoveCellClicked = (
        index: number,
        removeCellIdentifier?: string,
        reset?: UseFormReset<FieldValues>,
        values?: FieldValues
    ) => {
        if (values && reset && removeCellIdentifier) {
            const [originalPublisherIndex, administratorIndex] = removeCellIdentifier
                .split('-')
                .map((val) => Number.parseInt(val, 10))

            if (administratorIndex !== undefined) {
                const currentPublisher = originPublishers[originalPublisherIndex]
                const administratorToRemove = currentPublisher.administratorPublishers[administratorIndex]

                const updatedWorkDraft = adminPublisherRemover(
                    administratorToRemove,
                    currentPublisher,
                    workDraftFromLocal
                )
                workStore.setWorkDraftToLocal(updatedWorkDraft)
            } else {
                const publisherToRemove = originPublishers[originalPublisherIndex]
                removeOriginalPublisher(publisherToRemove)
            }
        }
    }

    return (
        <>
            <TablesForm
                className="add-publishers-table"
                headingDecoration={{
                    icon: 'publishers',
                    title: 'Publishers',
                    required: true,
                    toolTip: <CustomToolTip inverse type="?" info={TOOLTIP_TEXT.createWork.publisher} />,
                    rightData: <SharesInfo totalShares={calculateTotalKnownShares(workDraftFromLocal)} />,
                }}
                tableInfos={AddPublishersTableInfo()}
                addButtonTitle="Add Publisher"
                onAddButtonClicked={() => {
                    popupStore.setPublisherSidebarState(PublisherSideBarState.addOP)
                }}
                onRemoveCellClicked={getOnRemoveCellClicked}
                validationSchema={generatePublishersTableSchema(
                    workDraftFromLocal,
                    rightsholderStore.currentRightsholderId
                )}
                rowsAreValid={(value) => {
                    setTableIsValid(value)
                }}
                shouldFocusError={false}
            />
        </>
    )
}

export default observer(AddPublishersTable)
